import { findBookmarkName } from "@via/pages/data/file-organizer/helpers.ts";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import { FileOrganizerProvider } from "@via/context/file-organizer-context.tsx";
import { useUrlDataConnection } from "@via/hooks/useUrlDataConnection.tsx";
import UrlDataConnector from "@via/pages/data/data-sources/api-data-sources/url/url-data-connector.tsx";
import DataSourceTitle from "@via/pages/data/data-sources/data-source-title.tsx";
import { useUrlFolderList } from "@via/pages/data/data-sources/data-source.api.tsx";
import FileOrganizer from "@via/pages/data/file-organizer/file-organizer.tsx";
import { DataSourceComponentView } from "@via/utils/interfaces.tsx";
import type { SelectedFile } from "common";
import { DataSourceProvider, FileListFromWebRequestBody } from "common";
import { type JSX, useEffect, useState } from "react";
import { isValidUrl } from "@via/utils/helpers.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import UrlFolderBrowser from "@via/pages/data/data-sources/api-data-sources/url/url-folder-browser.tsx";
import { useSearchParams } from "react-router-dom";
import { useGetDataSourceList } from "@via/pages/data/data-connections/data-connections.api.tsx";
import { BucketObject } from "@via/pages/data/data-sources/utils/helpers.tsx";

export default function UrlData(): JSX.Element {
  const [searchParams] = useSearchParams();
  const bookmarkId = searchParams.get("bookmark");
  const { data: bookmarkList } = useGetDataSourceList();
  const { setStarterUrl } = useUrlDataConnection();

  useEffect(() => {
    if (bookmarkId && bookmarkList?.length) {
      const selectedDatasource = bookmarkList.find(
        item => item.id === Number(bookmarkId)
      );

      const bookmarkUrl = selectedDatasource?.path || "";
      setStarterUrl(bookmarkUrl);
    }
  }, [bookmarkId, bookmarkList]);

  const { setNotification, NOTIFICATION } = useNotifications();
  const [currentView, setCurrentView] = useState<DataSourceComponentView>(
    DataSourceComponentView.FolderBrowser
  );
  const [hasConnection, setHasConnection] = useState(false);

  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [cart, setCart] = useState<BucketObject[]>([]);
  const { connectionCredentials, setConnectionCredentials } =
    useUrlDataConnection();

  const updateSelectedFiles = (selectedFileList: SelectedFile[]): void => {
    setGlobalBucketName(
      connectionCredentials?.bucketName
        ? `${connectionCredentials.bucketName}`
        : ""
    );
    setSelectedFiles(
      selectedFileList.map(file => ({
        ...file,
        id: file.name,
      }))
    );
    setCurrentView(DataSourceComponentView.FileOrganizer);
  };

  const [globalBucketName, setGlobalBucketName] = useState("");

  const setCurrentViewToFolderBrowser = () => {
    setCurrentView(DataSourceComponentView.FolderBrowser);
  };

  const {
    mutateAsync: getUrlFolderList,
    isPending: isGetUrlFolderListLoading,
    isSuccess: isGetUrlFolderListSuccess,
  } = useUrlFolderList();

  const handleConnectorFormSubmit = async (
    payload: FileListFromWebRequestBody
  ) => {
    if (isValidUrl(payload.url)) {
      const getUrlFolderListPayload = {
        bucketName: payload.url,
      };
      await getUrlFolderList(getUrlFolderListPayload);
      setConnectionCredentials(getUrlFolderListPayload);
    } else {
      setNotification({
        id: "invalid-url",
        message: "Invalid URL",
        details: "Entered input is not in a valid URL format",
        variant: NOTIFICATION.ERROR,
      });
    }
  };

  useEffect(() => {
    setHasConnection(isGetUrlFolderListSuccess);
  }, [isGetUrlFolderListSuccess]);

  return (
    <div className="flex-auto">
      <div className="ml-2">
        <DataSourceTitle
          icon={<GlobeAltIcon className="mr-3 h-10 w-10" />}
          title="Web URL"
        />

        {currentView === DataSourceComponentView.FileOrganizer ? (
          <FileOrganizerProvider>
            <FileOrganizer
              importedFiles={selectedFiles}
              globalBucketName={globalBucketName}
              onBackToSelectionClick={setCurrentViewToFolderBrowser}
              credentialId={null}
              dataSourceName={findBookmarkName(bookmarkList, bookmarkId)}
              provider={DataSourceProvider.URL}
            />
          </FileOrganizerProvider>
        ) : (
          <div>
            {hasConnection ? (
              <UrlFolderBrowser
                onContinue={updateSelectedFiles}
                cart={cart}
                setCart={setCart}
              />
            ) : (
              <UrlDataConnector
                onConnectorFormSubmit={handleConnectorFormSubmit}
                isGetUrlFolderListLoading={isGetUrlFolderListLoading}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
